// src/store/slices/userProfileSlice.ts (Corrected - No Arguments)
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { DashboardApi } from '../../apis/DashboardApi' // Adjust path as needed

// Define the user profile interface based on your API response
export interface UserData {
  id: number
  first_name: string
  last_name: string
  middle_name?: string
  gender?: string | null
  phone_number: string
  user_id: string
  referral_id: string
  referrer: string
  isVerified: string
  role: string
  email: string
  email_verified_at: string | null
  created_at: string
  updated_at: string
  date_of_birth: string
  place_of_birth?: string | null
  state_of_origin?: string | null
  nationality: string
  nin_number: string
  occupation: string
  employment_status?: string | null
  level_of_education?: string | null
  address: string
  utility_type?: string | null
  profile_picture: string
  profile_picture_id: string
  nin_picture?: string | null
  nin_picture_id?: string | null
  utility_bill_picture?: string | null
  utility_bill_picture_id?: string | null
  status: string
  payment_id?: string | null
  profile_photo?: string | null
  registration_status: string
  country?: string | null
  city: string
  state: string
  zip_code: string
  otp_expires_at: string
  bvn: string
  purpose_for_using: string
  source_of_funds?: string | null
  income_range: string
  next_of_kin_name: string
  next_of_kin_phone: string
  next_of_kin_email?: string | null
  next_of_kin_relationship: string
  afripay_tag: string
  transaction_pin_set: boolean
  maplerad_customer_id: string
  maplerad_enrolled_at: string
}

export interface UserProfile {
  token: string
  name: string
  role: string
  has_usd_card: boolean
  account_id: number
  user_data: UserData
  has_profile_picture: boolean
  profile_picture_url: string
}

// Define the initial state interface
interface UserProfileState {
  profile: UserProfile | null
  loading: boolean
  error: string | null
  lastFetched: number | null
}

// Initial state
const initialState: UserProfileState = {
  profile: null,
  loading: false,
  error: null,
  lastFetched: null
}

// Async thunk for fetching user profile - NO ARGUMENTS NEEDED
export const fetchUserProfile = createAsyncThunk<UserProfile, void>(
  'userProfile/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      // Get token from localStorage (we know this exists)
      const token = localStorage.getItem('afripay_token')
      // console.log('Token available:', !!token)
      
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Make direct API call with axios using your correct API URL
      const axios = (await import('axios')).default
      
      const response = await axios.get('https://api.goafripay.com/api/user/profile', {
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json",
          "Authorization": `Bearer ${token}`,
          "Access-Control-Allow-Origin": "*" ,
          "X-Browser-Id": "client_H6C2q86BqhICuaQ6naZFzV2qUfodAnLN"
        },
      })
      
      // console.log('API Response:', response.data)
      
      // Check if the API call was successful
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch user profile')
      }

      return response.data.data // Return the data object from your API response
    } catch (error: any) {
      console.error('fetchUserProfile error:', error)
      
      // Handle axios errors
      if (error.response) {
        console.error('Error response status:', error.response.status)
        console.error('Error response data:', error.response.data)
        return rejectWithValue(error.response.data.message || 'Failed to fetch user profile')
      }
      return rejectWithValue(error.message || 'Unknown error')
    }
  }
)

// Create the user profile slice
const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    // Clear profile data (useful for logout)
    clearProfile: (state) => {
      state.profile = null
      state.error = null
      state.lastFetched = null
    },
    
    // Update entire user_data object - safest approach
    updateUserData: (state, action: PayloadAction<Partial<UserData>>) => {
      if (state.profile?.user_data) {
        state.profile.user_data = {
          ...state.profile.user_data,
          ...action.payload
        }
      }
    },
    
    // Update entire profile object
    updateProfile: (state, action: PayloadAction<Partial<UserProfile>>) => {
      if (state.profile) {
        state.profile = {
          ...state.profile,
          ...action.payload
        }
      }
    },
    
    // Specific updaters for common use cases
    updateBasicInfo: (state, action: PayloadAction<{
      first_name?: string
      last_name?: string
      middle_name?: string
      email?: string
      phone_number?: string
    }>) => {
      if (state.profile?.user_data) {
        Object.assign(state.profile.user_data, action.payload)
      }
    },
    
    updateAddress: (state, action: PayloadAction<{
      address?: string
      city?: string
      state?: string
      zip_code?: string
      country?: string
    }>) => {
      if (state.profile?.user_data) {
        Object.assign(state.profile.user_data, action.payload)
      }
    },
    
    updateVerificationStatus: (state, action: PayloadAction<{
      isVerified?: string
      status?: string
      registration_status?: string
    }>) => {
      if (state.profile?.user_data) {
        Object.assign(state.profile.user_data, action.payload)
      }
    },
    
    // Clear any errors
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    // Fetch user profile cases
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload
        state.lastFetched = Date.now()
        state.error = null
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

// Export actions
export const { 
  clearProfile, 
  updateUserData, 
  updateProfile,
  updateBasicInfo,
  updateAddress,
  updateVerificationStatus,
  clearError 
} = userProfileSlice.actions

// Export selectors
export const selectUserProfile = (state: { userProfile: UserProfileState }) => state.userProfile.profile
export const selectUserData = (state: { userProfile: UserProfileState }) => state.userProfile.profile?.user_data
export const selectUserProfileLoading = (state: { userProfile: UserProfileState }) => state.userProfile.loading
export const selectUserProfileError = (state: { userProfile: UserProfileState }) => state.userProfile.error
export const selectLastFetched = (state: { userProfile: UserProfileState }) => state.userProfile.lastFetched

// Helper selectors for commonly used profile data
export const selectUserName = (state: { userProfile: UserProfileState }) => {
  const userData = state.userProfile.profile?.user_data
  return userData ? `${userData.first_name} ${userData.last_name}` : ''
}

export const selectUserEmail = (state: { userProfile: UserProfileState }) => 
  state.userProfile.profile?.user_data?.email

export const selectUserPhone = (state: { userProfile: UserProfileState }) => 
  state.userProfile.profile?.user_data?.phone_number

export const selectProfilePicture = (state: { userProfile: UserProfileState }) => 
  state.userProfile.profile?.profile_picture_url

export const selectAfriPayTag = (state: { userProfile: UserProfileState }) => 
  state.userProfile.profile?.user_data?.afripay_tag

export const selectAccountId = (state: { userProfile: UserProfileState }) => 
  state.userProfile.profile?.account_id

export const selectIsVerified = (state: { userProfile: UserProfileState }) => 
  state.userProfile.profile?.user_data?.isVerified === 'true'

// Helper selector to check if profile data is stale (older than 5 minutes)
export const selectIsProfileDataStale = (state: { userProfile: UserProfileState }) => {
  const lastFetched = state.userProfile.lastFetched
  if (!lastFetched) return true
  
  const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
  return lastFetched < fiveMinutesAgo
}

// Export the reducer
export default userProfileSlice.reducer